import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw, Table2, Swords, Upload, ExternalLink, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatINRShort } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MilestoneBreakdown } from "@/components/MilestoneBreakdown";

const ALL = "all";
const cr = (v) => (v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const MONTH_LABEL = (m) => m === "all" ? "All Months" : new Date(`${m}-01`).toLocaleString("en-GB", { month: "short", year: "2-digit" });
const MONTH_LABEL_FULL = (m) => m === "all" ? "All Months" : new Date(`${m}-01`).toLocaleString("en-GB", { month: "long", year: "numeric" });

function Panel({ title, children, onClick, testid, footer }) {
  return (
    <Card className={`p-4 flex flex-col ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`} onClick={onClick} data-testid={testid}>
      <p className="text-sm uppercase tracking-wider text-slate-600 font-bold mb-2">{title}</p>
      <div className="flex-1">{children}</div>
      {footer && <div className="mt-3 pt-2 border-t border-slate-100 text-sm font-semibold text-slate-600">{footer}</div>}
    </Card>
  );
}

function TargetBars({ ytd, month, monthLabel }) {
  const data = [
    { name: "YTD", Target: ytd.target, Achieved: ytd.achieved },
    { name: monthLabel, Target: month.target, Achieved: month.achieved },
  ];
  const cf = Number(month.carry_forward || 0);
  const lbl = { position: "top", fontSize: 16, fontWeight: 800, fill: "#0f172a", formatter: (v) => v };
  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={data} margin={{ top: 22, right: 4, left: -18, bottom: 0 }}>
        <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#475569", fontWeight: 700 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v, n, p) => (n === "Target" && p?.payload?.name === monthLabel && cf
          ? `${cr(v)} (${cr(month.base)} target ${cf > 0 ? "+" : "−"} ${cr(Math.abs(cf))} ${cf > 0 ? "shortfall" : "surplus"} carried forward)` : cr(v))}
          cursor={{ fill: "#f8fafc" }} />
        <Bar dataKey="Target" fill="#f59e0b" radius={[3, 3, 0, 0]} label={lbl} />
        <Bar dataKey="Achieved" fill="#10b981" radius={[3, 3, 0, 0]} label={lbl} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CarryForwardNote({ block, monthLabel }) {
  const cf = Number(block.carry_forward || 0);
  if (!cf) return null;
  return (
    <p className="text-[11px] font-semibold text-slate-500 text-center" data-testid="bi-carry-forward">
      {monthLabel} target {cr(block.month_target_base)} {cf > 0 ? "+" : "−"} {cr(Math.abs(cf))} {cf > 0 ? "shortfall" : "surplus"} from earlier months = {cr(block.month_target)}
    </p>
  );
}

function AskRate() { return null; }

function WinLossPanel({ title, data, onClick, testid }) {
  const chart = [
    { name: "Value", Win: +((data.win.value || 0) / 1e7).toFixed(2), Loss: +((data.loss.value || 0) / 1e7).toFixed(2) },
    { name: "Count", Win: data.win.count, Loss: data.loss.count },
  ];
  return (
    <Panel title={title} onClick={onClick} testid={testid}>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={chart} margin={{ top: 22, right: 4, left: -18, bottom: 0 }}>
          <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#475569", fontWeight: 700 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: "#f8fafc" }} formatter={(v, n, p) => p?.payload?.name === "Value" ? `${v} (INR Cr)` : v} />
          <Bar dataKey="Win" fill="#10b981" radius={[3, 3, 0, 0]} label={{ position: "top", fontSize: 16, fontWeight: 800, fill: "#0f172a" }} />
          <Bar dataKey="Loss" fill="#ef4444" radius={[3, 3, 0, 0]} label={{ position: "top", fontSize: 16, fontWeight: 800, fill: "#0f172a" }} />
        </BarChart>
      </ResponsiveContainer>
      <p className="text-sm font-bold text-slate-600 text-center -mt-1">Value in INR Cr · Win vs Loss</p>
    </Panel>
  );
}

const FUNNEL_STYLES = [
  { from: "#f87171", to: "#b91c1c", glow: "rgba(185,28,28,0.35)" },
  { from: "#fb923c", to: "#c2410c", glow: "rgba(234,88,12,0.35)" },
  { from: "#fbbf24", to: "#d97706", glow: "rgba(217,119,6,0.35)" },
  { from: "#a3e635", to: "#4d7c0f", glow: "rgba(101,163,13,0.35)" },
  { from: "#34d399", to: "#059669", glow: "rgba(16,185,129,0.35)" },
  { from: "#2dd4bf", to: "#0f766e", glow: "rgba(13,148,136,0.35)" },
];
const FUNNEL_WIDTHS = [100, 89, 78, 67, 56, 45];

function FunnelPanel({ pipeline }) {
  const navigate = useNavigate();
  return (
    <Panel title="Pipeline Value | Count" testid="bi-funnel"
      footer={
        <div className="text-base font-semibold" data-testid="bi-funnel-footer">
          Opportunities Value – <span className="font-extrabold text-slate-900 text-xl">{formatINRShort(pipeline.pipeline_value)}</span>
          <span className="mx-1 text-slate-400">/</span>
          <span className="text-[11px] font-semibold text-slate-500">{pipeline.pipeline_count} (count)</span>
          <p className="text-[11px] font-medium text-slate-500 mt-0.5">(Total of Cold + Warm + Hot opportunities)</p>
        </div>
      }>
      <div className="flex flex-col justify-center h-full py-2 space-y-2">
        {pipeline.funnel.map((row, i) => {
          const s = FUNNEL_STYLES[i] || FUNNEL_STYLES[FUNNEL_STYLES.length - 1];
          return (
            <button key={row.key} onClick={() => navigate(row.link)}
              className="flex items-center gap-3 w-full group" data-testid={`bi-funnel-${row.key}`}>
              <div className="flex-1 flex justify-center">
                <div
                  className="h-12 flex items-center justify-center rounded-full text-white font-extrabold text-base md:text-lg tracking-wide transition-all duration-200 group-hover:scale-[1.03] group-hover:brightness-110"
                  style={{
                    width: `${FUNNEL_WIDTHS[i]}%`,
                    background: `linear-gradient(135deg, ${s.from} 0%, ${s.to} 100%)`,
                    boxShadow: `0 6px 16px -4px ${s.glow}, inset 0 1px 0 rgba(255,255,255,0.35)`,
                  }}>
                  <span className="drop-shadow-sm px-3 truncate">
                    {row.value ? `${formatINRShort(row.value)} | ${row.count}` : `${row.count.toLocaleString("en-IN")}`}
                  </span>
                </div>
              </div>
              <span className="text-sm md:text-base font-bold text-slate-700 w-44 shrink-0 text-left group-hover:text-brand">{row.label}</span>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

const PIE_COLORS = { "End Clients": "#10b981", Architects: "#6366f1", PMCs: "#f59e0b", Contractors: "#0ea5e9" };

function ContactsPanel({ contacts, monthLabel }) {
  const navigate = useNavigate();
  const data = Object.entries(contacts.by_type || {}).map(([name, value]) => ({ name, value }));
  return (
    <Panel title="Contacts Created" onClick={() => navigate("/contacts")} testid="bi-contacts"
      footer={<span className="text-base font-semibold">New eMail Ids <span className="font-extrabold text-slate-900 text-xl">{contacts.new_emails.toLocaleString("en-IN")}</span></span>}>
      {data.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No contacts created this FY yet.</p>
      ) : (
        <ResponsiveContainer width="100%" height={140}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={30} outerRadius={55} paddingAngle={2}>
              {data.map((d) => <Cell key={d.name} fill={PIE_COLORS[d.name] || "#94a3b8"} />)}
            </Pie>
            <Tooltip formatter={(v) => v.toLocaleString("en-IN")} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
      <div className="flex justify-center gap-2 mt-1">
        <span className="text-sm rounded bg-slate-100 px-2.5 py-1 text-slate-600">FY: <b className="text-slate-900">{contacts.fy_total.toLocaleString("en-IN")}</b></span>
        <span className="text-sm rounded bg-slate-100 px-2.5 py-1 text-slate-600">{monthLabel}: <b className="text-slate-900">{contacts.month_total.toLocaleString("en-IN")}</b></span>
      </div>
    </Panel>
  );
}

function PotentialsPanel({ potentials }) {
  const navigate = useNavigate();
  const rows = [
    ["Total", potentials.total, "bg-white"],
    ["Current (≤30d)", potentials.current, "bg-emerald-50"],
    [">30 days", potentials.gt30, "bg-sky-50"],
    [">60 days", potentials.gt60, "bg-amber-50"],
    [">90 days", potentials.gt90, "bg-red-50"],
    ["Past closing dt", potentials.past_close, "bg-red-50"],
    ["No follow-up 30d+", potentials.no_followup, "bg-yellow-50"],
  ];
  return (
    <Panel title="Potentials Status" onClick={() => navigate("/deals")} testid="bi-potentials">
      <table className="w-full text-[15px] [&_td]:border-r [&_td]:border-slate-100 [&_td:last-child]:border-r-0">
        <thead><tr className="text-slate-400 text-left text-xs uppercase tracking-wide">
          <th className="font-semibold pb-1">Category</th><th className="font-semibold pb-1 text-right">Count</th><th className="font-semibold pb-1 text-right">Value</th>
        </tr></thead>
        <tbody>
          {rows.map(([label, d, bg]) => (
            <tr key={label} className={`${bg} border-t border-slate-100`}>
              <td className="py-1.5 px-1 text-slate-600 font-semibold">{label}</td>
              <td className="py-1.5 px-1 text-right font-extrabold text-slate-900 text-base">{d.count}</td>
              <td className="py-1.5 px-1 text-right font-extrabold text-slate-900 text-base">{formatINRShort(d.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function EmptyManual({ label, canEdit }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
      <p className="text-sm text-slate-400">No data — upload pending</p>
      {canEdit && (
        <Link to="/sales-bi-data" onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-brand hover:underline" data-testid={`bi-upload-link-${label}`}>
          <Upload className="h-3.5 w-3.5" /> Add via Utility → Sales BI Data
        </Link>
      )}
    </div>
  );
}

function PendingBillingPanel({ data, canEdit }) {
  const total = data?.total;
  return (
    <Card className="p-4" data-testid="bi-pending-billing">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm uppercase tracking-wider text-slate-600 font-bold">Pending Billing Status (Value)</p>
        {total == null || total === "" ? (
          canEdit ? (
            <Link to="/sales-bi-data" onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand hover:underline" data-testid="bi-upload-link-pending">
              <Upload className="h-3.5 w-3.5" /> Add via Utility → Sales BI Data
            </Link>
          ) : <span className="text-sm text-slate-400">No data — upload pending</span>
        ) : (
          <p className="text-4xl font-bold text-slate-900 font-heading" data-testid="bi-pending-value">{Number(total).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</p>
        )}
      </div>
    </Card>
  );
}

function CompetitionDialog({ open, onOpenChange, rows, canEdit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bi-competition-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Swords className="h-4 w-4 text-slate-400" /> Competition Landscape</DialogTitle></DialogHeader>
        {(!rows || rows.length === 0) ? <EmptyManual label="competition" canEdit={canEdit} /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wider text-slate-500 text-left border-b border-slate-200">
              <th className="py-2">Competitor</th><th>Region</th><th>Product</th><th>Remarks</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 font-medium text-slate-800">{r.competitor}</td>
                  <td className="text-slate-600">{r.region || "—"}</td>
                  <td className="text-slate-600">{r.product || "—"}</td>
                  <td className="text-slate-500 text-xs">{r.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SalesBIDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [month, setMonth] = useState("");
  const [zone, setZone] = useState(ALL);
  const [branch, setBranch] = useState(ALL);
  const [person, setPerson] = useState(ALL);
  const [segment, setSegment] = useState(ALL);
  const [productType, setProductType] = useState(ALL);
  const [vertical, setVertical] = useState(ALL);
  const [productCategory, setProductCategory] = useState(ALL);
  const [compOpen, setCompOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const canEdit = ["super_admin", "sales_head", "management"].includes(user?.role);

  const openMilestonePdf = async () => {
    setPdfBusy(true);
    try {
      const { data: rep } = await api.get("/milestone-pdf/reports");
      if (!rep?.latest?.id) { toast.info("No Milestone PDF received yet"); return; }
      const res = await api.get(`/milestone-pdf/reports/${rep.latest.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch {
      toast.error("Could not open the Milestone PDF");
    } finally { setPdfBusy(false); }
  };

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["sales-bi", month, zone, branch, person, segment, productType, vertical, productCategory],
    queryFn: async () => (await api.get("/sales-bi/dashboard", { params: {
      month, zone: zone === ALL ? "" : zone, branch: branch === ALL ? "" : branch,
      person: person === ALL ? "" : person, segment: segment === ALL ? "" : segment,
      vertical: vertical === ALL ? "" : vertical, product_category: productCategory === ALL ? "" : productCategory,
      product_type: productType === ALL ? "" : productType } })).data,
  });

  if (isLoading || !data) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const f = data.filters;
  const mLabel = MONTH_LABEL(data.month);
  const mLabelFull = MONTH_LABEL_FULL(data.month);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="mr-auto">
          <h1 className="font-heading text-3xl font-extrabold text-slate-900" data-testid="bi-title">Sales Dashboard</h1>
          <p className="text-sm text-slate-500 font-medium">FY {data.fy} · live from CRM, billing & daily order booking data · All figures are in INR Cr</p>
        </div>
        <Select value={data.fy} onValueChange={() => {}}>
          <SelectTrigger className="h-9 w-[110px]" data-testid="bi-year"><SelectValue /></SelectTrigger>
          <SelectContent>{f.fys.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={data.month} onValueChange={setMonth}>
          <SelectTrigger className="h-9 w-[120px]" data-testid="bi-month"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {f.months.map((m) => <SelectItem key={m} value={m}>{MONTH_LABEL(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => refetch()} disabled={isFetching} data-testid="bi-refresh">
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {["super_admin", "sales_head", "management"].includes(user?.role) && (
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={openMilestonePdf} disabled={pdfBusy} data-testid="bi-milestone-pdf-btn">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Milestone PDF
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => navigate("/sales-mis")} data-testid="bi-mis-btn">
          <Table2 className="h-4 w-4" /> Sales MIS
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={zone} onValueChange={setZone}>
          <SelectTrigger className="h-9 w-[130px]" data-testid="bi-filter-zone"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All zones</SelectItem>{f.zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="bi-filter-branch"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All branches</SelectItem>{f.branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={person} onValueChange={setPerson}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="bi-filter-person"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All sales persons</SelectItem>{f.persons.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={segment} onValueChange={setSegment}>
          <SelectTrigger className="h-9 w-[130px]" data-testid="bi-filter-segment"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All segments</SelectItem>{f.segments.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={productType} onValueChange={setProductType}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="bi-filter-product"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All product types</SelectItem>{f.product_types.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        {f.verticals?.length > 0 && (
          <Select value={vertical} onValueChange={setVertical}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="bi-filter-vertical"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All verticals</SelectItem>{f.verticals.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {f.product_categories?.length > 0 && (
          <Select value={productCategory} onValueChange={setProductCategory}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="bi-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All product categories</SelectItem>{f.product_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" className="h-9 gap-1.5 ml-auto" onClick={() => setCompOpen(true)} data-testid="bi-competition-btn">
          <Swords className="h-4 w-4" /> Competition Landscape
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
            <Panel title="Booking Target vs Achieved" testid="bi-booking" onClick={() => navigate("/erp-sync-log")}>
              <TargetBars ytd={{ target: data.booking.ytd_target, achieved: data.booking.ytd_achieved }}
                month={{ target: data.booking.month_target, achieved: data.booking.month_achieved, base: data.booking.month_target_base, carry_forward: data.booking.carry_forward }} monthLabel={mLabelFull} />
              <p className="text-sm font-bold text-slate-600 text-center -mt-1">INR Cr · booking as on {data.booking.as_on || "—"}</p>
              <CarryForwardNote block={data.booking} monthLabel={mLabelFull} />
            </Panel>

            <Panel title="Billing Target vs Achieved" testid="bi-billing" onClick={() => navigate("/erp-sync-log")}>
              <TargetBars ytd={{ target: data.billing.ytd_target, achieved: data.billing.ytd_achieved }}
                month={{ target: data.billing.month_target, achieved: data.billing.month_achieved, base: data.billing.month_target_base, carry_forward: data.billing.carry_forward }} monthLabel={mLabelFull} />
              <p className="text-sm font-bold text-slate-600 text-center -mt-1">INR Cr · billing (basic value){data.billing.as_on ? ` · as on ${data.billing.as_on}` : ""}</p>
              <CarryForwardNote block={data.billing} monthLabel={mLabelFull} />
            </Panel>
          </div>
          <PendingBillingPanel data={data.pending_billing} canEdit={canEdit} />
        </div>
        <FunnelPanel pipeline={data.pipeline} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <WinLossPanel title="Win Loss Ratio FY" data={data.win_loss_fy} onClick={() => navigate("/deals")} testid="bi-winloss-fy" />
        <WinLossPanel title={`Win Loss Ratio ${mLabel}`} data={data.win_loss_month} onClick={() => navigate("/deals")} testid="bi-winloss-month" />
        <ContactsPanel contacts={data.contacts} monthLabel={mLabel} />
        <PotentialsPanel potentials={data.potentials} />
      </div>

      {f.milestone_dims && <MilestoneBreakdown fy={data.fy} month={data.month} filters={{ zone: zone === ALL ? "" : zone, branch: branch === ALL ? "" : branch, vertical: vertical === ALL ? "" : vertical, product_category: productCategory === ALL ? "" : productCategory }} />}

      <CompetitionDialog open={compOpen} onOpenChange={setCompOpen} rows={data.competition} canEdit={canEdit} />
    </div>
  );
}
